import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import InstallPrompt from './components/InstallPrompt';
import EmailCapturePopup from './components/EmailCapturePopup';
import { captureUtmFromUrl } from './lib/utmTracker';

// Capture UTMs synchronously at module load — must run BEFORE any child page's
// useEffect can strip utm_* params from the URL (e.g., ProductsPage's setSearchParams).
captureUtmFromUrl();

// Pages
import LandingPage from './pages/LandingPage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import CustomerDashboard from './pages/CustomerDashboard';
import VendorDashboard from './pages/VendorDashboard';
import VendorHelpPage from './pages/VendorHelpPage';
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';
import AdminDashboard from './pages/AdminDashboard';
import AuthCallback from './pages/AuthCallback';
import VendorStorefront from './pages/VendorStorefront';
import HouseBrandStorefront from './pages/HouseBrandStorefront';
import VendorCovenantPage from './pages/VendorCovenantPage';
import VendorApplicationPage from './pages/VendorApplicationPage';
import AboutPage from './pages/AboutPage';
import OilAndGasPage from './pages/OilAndGasPage';
import InstallPage from './pages/InstallPage';
import FlutterwaveCompletePage from './pages/FlutterwaveCompletePage';
import MyDownloadsPage from './pages/MyDownloadsPage';

// Use same-origin backend when on a custom domain (avoids CORS issues with credentialed requests).
// Falls back to REACT_APP_BACKEND_URL for local dev and Emergent preview.
const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    // Same-origin for custom domains (anything that's not the Emergent platform itself)
    if (host && !host.includes('emergent') && host !== 'localhost' && !host.includes('127.0.0.1')) {
      return window.location.origin;
    }
  }
  return process.env.REACT_APP_BACKEND_URL;
};
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Add axios interceptor to include token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Analytics: Meta Pixel + GA4 (only loads if env IDs are set) ---
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

function loadAnalyticsScripts() {
  if (typeof window === 'undefined' || window.__ckmAnalyticsLoaded) return;
  window.__ckmAnalyticsLoaded = true;

  // Meta (Facebook) Pixel
  if (META_PIXEL_ID) {
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  }

  // Google Analytics 4
  if (GA4_MEASUREMENT_ID) {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA4_MEASUREMENT_ID);
  }
}

// Lightweight tracker used by components (no-ops if pixel/GA not configured)
export const track = (eventName, params = {}) => {
  try {
    if (typeof window === 'undefined') return;
    if (window.fbq) window.fbq('track', eventName, params);
    if (window.gtag) window.gtag('event', eventName, params);
  } catch (_) { /* swallow */ }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Cart Context
const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // Store tokens in localStorage
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name, role = 'customer') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    // Store tokens in localStorage
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  const googleLogin = async (sessionId) => {
    const response = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
    // Store tokens in localStorage
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    setUser(response.data);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, googleLogin, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

// Cart Provider
function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!user) {
      setCart({ items: [], total: 0, item_count: 0 });
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1, options = {}) => {
    const payload = { product_id: productId, quantity };
    if (options.variant_id !== undefined && options.variant_id !== null) {
      payload.variant_id = options.variant_id;
    }
    if (options.variant_label) {
      payload.variant_label = options.variant_label;
    }
    await axios.post(`${API}/cart/add`, payload);
    await fetchCart();
  };

  const updateCartItem = async (productId, quantity) => {
    await axios.put(`${API}/cart/update`, { product_id: productId, quantity });
    await fetchCart();
  };

  const removeFromCart = async (productId) => {
    await axios.delete(`${API}/cart/${productId}`);
    await fetchCart();
  };

  const clearCart = async () => {
    await axios.delete(`${API}/cart`);
    await fetchCart();
  };

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateCartItem, removeFromCart, clearCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
}

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// App Router with Auth Check
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/products/:productId" element={<ProductDetailPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/returns" element={<RefundPolicyPage />} />
      <Route path="/return-policy" element={<RefundPolicyPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/store/christ-kingdom-apparel" element={<HouseBrandStorefront />} />
      <Route path="/store/:slug" element={<VendorStorefront />} />
      <Route path="/vendor/covenant" element={<VendorCovenantPage />} />
      <Route path="/vendor/apply" element={<VendorApplicationPage />} />
      <Route path="/vendor/help" element={<VendorHelpPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/oil-and-gas" element={<OilAndGasPage />} />
      <Route path="/install" element={<InstallPage />} />
      <Route path="/payment/flutterwave/complete" element={<FlutterwaveCompletePage />} />
      
      {/* Protected Routes */}
      <Route path="/cart" element={
        <ProtectedRoute>
          <CartPage />
        </ProtectedRoute>
      } />
      <Route path="/checkout/success" element={
        <ProtectedRoute>
          <CheckoutSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/checkout/cancel" element={
        <ProtectedRoute>
          <CartPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <CustomerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/my-downloads" element={
        <ProtectedRoute>
          <MyDownloadsPage />
        </ProtectedRoute>
      } />
      <Route path="/vendor/*" element={
        <ProtectedRoute allowedRoles={['vendor', 'admin']}>
          <VendorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    loadAnalyticsScripts();
    captureUtmFromUrl();
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppRouter />
          <EmailCapturePopup />
          <Toaster position="top-right" richColors />
          <InstallPrompt />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
